import { create } from 'zustand';
import type { SitePublic } from '@wooconnect/shared';

type Theme = 'light' | 'dark' | 'system';

interface UIState {
  theme: Theme;
  sidebarOpen: boolean;
  language: string;
  activeSiteId: string | null;
  activeSite: SitePublic | null;

  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setLanguage: (lang: string) => void;
  setActiveSite: (site: SitePublic) => void;
  setActiveSiteId: (id: string | null) => void;
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    // system
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
}

const savedTheme = (localStorage.getItem('wca_theme') as Theme) ?? 'system';
const savedLang = localStorage.getItem('wca_language') ?? 'en';
const savedSiteId = localStorage.getItem('wca_active_site_id') ?? null;

// Apply theme immediately
applyTheme(savedTheme);

export const useUIStore = create<UIState>((set) => ({
  theme: savedTheme,
  sidebarOpen: false,
  language: savedLang,
  activeSiteId: savedSiteId,
  activeSite: null,

  setTheme: (theme) => {
    localStorage.setItem('wca_theme', theme);
    applyTheme(theme);
    set({ theme });
  },

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  setLanguage: (lang) => {
    localStorage.setItem('wca_language', lang);
    set({ language: lang });
  },

  setActiveSite: (site) => {
    localStorage.setItem('wca_active_site_id', site.id);
    set({ activeSite: site, activeSiteId: site.id });
  },

  setActiveSiteId: (id) => {
    if (id) localStorage.setItem('wca_active_site_id', id);
    else localStorage.removeItem('wca_active_site_id');
    set({ activeSiteId: id });
  },
}));
